import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Search, Mail } from 'lucide-react';
import { FASH_LOGO } from '../../constants/brand';
import { SHOPPER_HOME } from '../../constants/routes';
import { BackButton } from '../../components/ui/BackButton';
import { useAuth } from '../../hooks/useAuth';
import { Settings } from 'lucide-react';
import { ShopperNavActions } from '../../components/shopper/ShopperNavActions';
import { ShopperFooter } from '../../components/shopper/ShopperFooter';

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQSection {
  title: string;
  items: FAQItem[];
}

const faqSections: FAQSection[] = [
  {
    title: 'Getting Started with Fash',
    items: [
      {
        question: 'What is Fash and how does it work?',
        answer:
          'Fash is a fashion marketplace that connects you with local boutiques for same-day delivery. Browse curated collections from independent boutiques near you, add items to your bag, and get them delivered to your door — often within hours. Think of it as the convenience of online shopping with the charm and exclusivity of local boutique fashion.',
      },
      {
        question: 'Is Fash free to use?',
        answer:
          'Yes! Creating an account and browsing boutiques on Fash is completely free. You only pay for the items you purchase, plus any applicable delivery fees. We also regularly offer promotions like free same-day delivery on orders over a certain amount.',
      },
      {
        question: 'What cities is Fash available in?',
        answer:
          "Fash is currently available in select neighborhoods in New York City, including SoHo, the West Village, and Williamsburg. We're rapidly expanding to more neighborhoods and cities — follow us on social media or enable notifications to be the first to know when we launch near you.",
      },
      {
        question: 'Do I need an account to shop on Fash?',
        answer:
          "You can browse boutiques and products without an account, but you'll need to create a free account to add items to your bag, save favorites, and place orders. Signing up takes less than a minute with your email or Google account.",
      },
    ],
  },
  {
    title: 'Shopping on Fash',
    items: [
      {
        question: 'How do I find boutiques near me?',
        answer:
          'When you open Fash, we automatically show boutiques closest to your location. You can also browse the full boutique directory, search by name or style, and filter by distance. Each boutique page shows their full collection, hours, and how far they are from you.',
      },
      {
        question: 'Can I shop from multiple boutiques in one order?',
        answer:
          "Currently, each order is fulfilled by a single boutique to ensure the fastest possible delivery. If you'd like items from multiple boutiques, you can place separate orders and they'll each be delivered as soon as they're ready.",
      },
      {
        question: 'How do I save items I like?',
        answer:
          "Tap the heart icon on any product to add it to your Likes. You can view all your saved items on the Likes page, accessible from the bottom navigation. Likes are synced across all your devices when you're signed in.",
      },
      {
        question: 'Are the products on Fash authentic?',
        answer:
          "Absolutely. Every boutique on Fash is a verified, independent retailer. We personally vet each boutique before they join the platform to ensure they meet our standards for quality and authenticity. You're shopping directly from the boutique — we never hold inventory ourselves.",
      },
    ],
  },
  {
    title: 'Orders & Delivery',
    items: [
      {
        question: 'How fast is same-day delivery?',
        answer:
          "Most orders are delivered within 1–3 hours of being placed, depending on the boutique's proximity and order volume. You'll see an estimated delivery window at checkout, and you can track your order in real-time once it's been dispatched.",
      },
      {
        question: 'How much does delivery cost?',
        answer:
          'Delivery fees vary based on distance and order size, typically ranging from $5–$15. We frequently offer free delivery promotions — check the homepage for current offers. Orders over $100 often qualify for free same-day delivery.',
      },
      {
        question: 'Can I track my order?',
        answer:
          "Yes! Once your order is confirmed and a driver has been assigned, you can track your delivery in real-time on the order tracking page. You'll see the driver's location on a live map, estimated arrival time, and receive notifications at each stage.",
      },
      {
        question: "What if I'm not home when my order arrives?",
        answer:
          'Our drivers will attempt to contact you when they arrive. You can add delivery instructions at checkout (e.g., "leave with doorman" or "call when outside"). If delivery can\'t be completed, we\'ll work with you to reschedule.',
      },
      {
        question: 'What happens when I receive an order?',
        answer:
          "When your order arrives, the driver will hand it to you or follow your delivery instructions. You'll receive a confirmation notification, and you can rate your delivery experience. If anything is wrong with your order, you can open a dispute directly from the order details page.",
      },
    ],
  },
  {
    title: 'Payments',
    items: [
      {
        question: 'What payment methods does Fash accept?',
        answer:
          'Fash accepts all major credit and debit cards (Visa, Mastercard, American Express, Discover) as well as Apple Pay and Google Pay. All payments are securely processed through Stripe, so your card details are never stored on our servers.',
      },
      {
        question: 'When am I charged for my order?',
        answer:
          "Your payment method is authorized when you place your order and charged once the boutique confirms and prepares your items. If an item is unavailable, you'll only be charged for the items that are fulfilled.",
      },
      {
        question: 'Is my payment information secure?',
        answer:
          'Yes. We use Stripe, an industry-leading payment processor trusted by millions of businesses worldwide. All transactions are encrypted with TLS, and we are PCI-DSS compliant. We never store your full card number on our servers.',
      },
    ],
  },
  {
    title: 'Returns & Disputes',
    items: [
      {
        question: "What is Fash's return policy?",
        answer:
          "Return policies are set by each individual boutique and are displayed on the product detail page before you purchase. Most boutiques offer returns or exchanges within 14–30 days for unworn items with tags attached. Check the specific boutique's policy on their Fash storefront.",
      },
      {
        question: 'How do I return an item?',
        answer:
          'To initiate a return, go to your order details and tap "Open Dispute." Describe the issue and our team will coordinate with the boutique to arrange a return or exchange. In many cases, a driver can pick up the return from your location.',
      },
      {
        question: 'What if my order arrives damaged or incorrect?',
        answer:
          "We're sorry if that happens! Open a dispute from your order details page right away and include photos of the issue. Our support team reviews disputes quickly and will work with the boutique to resolve it — whether that means a replacement, exchange, or full refund.",
      },
    ],
  },
  {
    title: 'For Boutique Owners',
    items: [
      {
        question: 'How can my boutique join Fash?',
        answer:
          "We'd love to have you! Visit the Business portal and complete the onboarding application. You'll provide details about your boutique, upload your product catalog, and set your delivery preferences. Our team reviews applications within 2–3 business days.",
      },
      {
        question: 'What does it cost to sell on Fash?',
        answer:
          "There's no upfront fee to join Fash. We operate on a commission model — we take a small percentage of each sale made through the platform. You'll see the full fee structure during onboarding. There are no monthly subscription fees or hidden charges.",
      },
      {
        question: 'How do I manage my inventory on Fash?',
        answer:
          "Once onboarded, you'll have access to the Business Dashboard where you can add, edit, and remove products, manage pricing, track orders, and view analytics. You can update your inventory in real-time to ensure customers always see what's available.",
      },
      {
        question: 'How do payouts work?',
        answer:
          'Your share of an order reaches your Stripe balance once the courier collects it. Stripe then deposits it into your linked bank account on the schedule set in your Stripe dashboard. Payouts require complete taxpayer information on file (EIN or SSN, tax address, authorized representative, and a signed W-9) — until that is done, FASH holds your proceeds and releases them automatically once it is complete. You can see your payout history and anything currently held in the Business Dashboard.',
      },
    ],
  },
];

function AccordionItem({ item }: { item: FAQItem }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-gray-100 last:border-b-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-start justify-between py-3 text-left group"
      >
        <span className="text-xs md:text-sm font-medium text-heading pr-6 group-hover:text-primary transition-colors">
          {item.question}
        </span>
        <ChevronDown
          className={`h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          isOpen ? 'max-h-96 pb-3' : 'max-h-0'
        }`}
      >
        <p className="text-xs md:text-sm text-body leading-relaxed pr-6">
          {item.answer}
        </p>
      </div>
    </div>
  );
}

export function FAQPage() {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();

  // When navigating to /faq#returns, scroll the Returns section into view and highlight it
  useEffect(() => {
    if (location.hash !== '#returns') return;
    const el = document.getElementById('returns');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      el.classList.add('ring-2', 'ring-primary/30', 'ring-offset-4', 'rounded-xl');
      const t = setTimeout(() => {
        el.classList.remove('ring-2', 'ring-primary/30', 'ring-offset-4', 'rounded-xl');
      }, 3000);
      return () => clearTimeout(t);
    }
  }, [location.hash]);

  const filteredSections = searchQuery.trim()
    ? faqSections
        .map((section) => ({
          ...section,
          items: section.items.filter(
            (item) =>
              item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
              item.answer.toLowerCase().includes(searchQuery.toLowerCase())
          ),
        }))
        .filter((section) => section.items.length > 0)
    : faqSections;

  const totalResults = filteredSections.reduce(
    (sum, s) => sum + s.items.length,
    0
  );

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 py-2.5 md:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <BackButton
                fallback={SHOPPER_HOME}
                className="p-1.5 md:p-2 hover:bg-gray-100 rounded-full transition-colors"
                iconClassName="h-5 w-5 md:h-6 md:w-6 text-heading"
              />
              <Link to={SHOPPER_HOME}>
                <img
                  src={FASH_LOGO}
                  alt="Fash"
                  className="h-12 md:h-16 object-contain"
                />
              </Link>
            </div>
            <div className="flex items-center space-x-2 md:space-x-4">
              {user ? (
                <Link
                  to="/settings"
                  className="p-1.5 md:p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <Settings className="h-5 w-5 md:h-6 md:w-6 text-heading" />
                </Link>
              ) : (
                <Link to="/signin">
                  <span className="text-xs md:text-sm font-medium text-heading hover:text-primary transition-colors">
                    Sign In
                  </span>
                </Link>
              )}
              <ShopperNavActions />
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 md:px-6">
        {/* Hero */}
        <div className="pt-5 md:pt-8 pb-4 md:pb-6 text-center">
          <h1 className="text-2xl md:text-4xl font-serif text-heading mb-1.5 md:mb-2">
            Frequently Asked Questions
          </h1>
          <p className="text-xs md:text-base text-body max-w-xl mx-auto">
            Find answers to the most common questions below.
          </p>
        </div>

        {/* Search */}
        <div className="relative mb-5 md:mb-8">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 md:h-5 md:w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search questions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 md:pl-11 pr-4 h-10 md:h-12 rounded-full border border-gray-200 bg-gray-50 text-xs md:text-sm text-heading placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
          />
          {searchQuery && (
            <p className="mt-2 text-xs text-body text-center">
              {totalResults} result{totalResults !== 1 ? 's' : ''} found
            </p>
          )}
        </div>

        {/* FAQ Sections */}
        <div className="space-y-5 md:space-y-8 pb-8">
          {filteredSections.map((section) => (
            <section
              key={section.title}
              id={section.title === 'Returns & Disputes' ? 'returns' : undefined}
              className="scroll-mt-20 transition-shadow duration-300"
            >
              <h2 className="text-sm md:text-lg font-serif text-heading mb-0.5 md:mb-1">
                {section.title}
              </h2>
              <div className="border-t border-gray-200">
                {section.items.map((item) => (
                  <AccordionItem key={item.question} item={item} />
                ))}
              </div>
            </section>
          ))}

          {searchQuery && filteredSections.length === 0 && (
            <div className="text-center py-12">
              <p className="text-lg font-medium text-heading mb-2">
                No results found
              </p>
              <p className="text-sm text-body">
                Try a different search term, or browse all questions by clearing
                the search.
              </p>
            </div>
          )}
        </div>

        {/* Still have questions CTA */}
        <div className="border-t border-gray-200 py-6 md:py-8 text-center">
          <h3 className="text-base md:text-lg font-serif text-heading mb-1">
            Still have questions?
          </h3>
          <p className="text-xs md:text-sm text-body mb-3 max-w-md mx-auto">
            Our support team is happy to help.
          </p>
          <a
            href="mailto:support@fashapp.com"
            className="inline-flex items-center gap-1.5 bg-heading text-white text-xs md:text-sm font-medium px-5 py-2.5 rounded-full hover:bg-gray-800 transition-colors"
          >
            <Mail className="h-3.5 w-3.5" />
            Contact Support
          </a>
        </div>
      </main>

      <ShopperFooter className="hidden md:block" />
    </div>
  );
}
